import { useEffect, useState } from 'react'

/**
 * Theme picker. UI-only — persisted in localStorage under tradeski.appearance
 * per §8.7 / §14 decision 9. Never sent to the server. Density is fixed at
 * `compact` (set on documentElement at boot) and no longer user-configurable;
 * the surface is named "Theme" in the UI.
 */

type Theme = 'light' | 'dark' | 'system'
type Appearance = { theme: Theme }

const STORAGE_KEY = 'tradeski.appearance'
const DEFAULT: Appearance = { theme: 'system' }

const applyTheme = (theme: Theme) => {
    const resolved =
        theme === 'system'
            ? window.matchMedia('(prefers-color-scheme: dark)').matches
                ? 'dark'
                : 'light'
            : theme
    document.documentElement.dataset.theme = resolved
}

const load = (): Appearance => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (!raw) return DEFAULT
        return { ...DEFAULT, ...(JSON.parse(raw) as Partial<Appearance>) }
    } catch {
        return DEFAULT
    }
}

const save = (a: Appearance) => localStorage.setItem(STORAGE_KEY, JSON.stringify(a))

export const initAppearance = () => {
    const a = load()
    applyTheme(a.theme)
    document.documentElement.dataset.density = 'compact'
}

const themeOptions: { value: Theme; label: string; hint: string }[] = [
    { value: 'light', label: 'Light', hint: 'Sapphire on white' },
    { value: 'dark', label: 'Dark', hint: 'Deep slate + sky' },
    { value: 'system', label: 'System', hint: 'Match OS' },
]

export const AppearancePanel = () => {
    const [appearance, setAppearance] = useState<Appearance>(load)

    useEffect(() => {
        save(appearance)
        applyTheme(appearance.theme)
    }, [appearance])

    return (
        <div className="panel">
            <div className="panel-header">
                <div>
                    <div className="eyebrow mb-1">Appearance</div>
                    <h2 className="panel-title">Theme</h2>
                </div>
            </div>

            <div>
                <p className="field-label">Theme</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {themeOptions.map((t) => {
                        const active = appearance.theme === t.value
                        return (
                            <button
                                key={t.value}
                                className={[
                                    'card surface-card text-left p-3 transition-all focusable',
                                    active ? 'shadow-yellow border-yellow' : 'hover:border-strong',
                                ].join(' ')}
                                onClick={() => setAppearance({ ...appearance, theme: t.value })}
                            >
                                <div className="display text-sm">{t.label}</div>
                                <div className="text-xs text-muted mt-0.5">{t.hint}</div>
                            </button>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}
